import React, { useEffect, useRef } from 'react';

import { useForm } from 'react-hook-form';

import { useTranslation } from 'react-i18next';

import { FormControl, Stack } from '@mui/material';

import useCommentTemplates from '../../hooks/commentTemplates/useCommentTemplates';

import type {
    CommentTemplate,
    CommentTemplateCreateProps,
} from '../../models/CommentTemplate';
import type { GeneralDialogComponentProps } from '../../models/GeneralDialogProps';

import { yupResolver } from '@hookform/resolvers/yup/dist/yup';

import TextFieldControlled from 'components/shared/TextFieldControlled';
import commentTemplateSchema from 'schemas/commentTempalteSchema';
import SnackbarUtils from '../../utils/SnackbarUtils';

export const AddUpdateCommentTemplate: React.FC<
    GeneralDialogComponentProps<CommentTemplate>
> = (props) => {
    const firstRun = useRef<boolean>(true);

    const { triggerChild, closeDialog, preSetContent } = props;

    const { t } = useTranslation('quotations', {
        keyPrefix: 'QUOTATIONS.QUOTATION_EDITOR.COMMENT_TEMPLATE.ADD_NEW',
    });

    const commentTemplate = useCommentTemplates();

    const form = useForm<CommentTemplateCreateProps>({
        resolver: yupResolver(commentTemplateSchema(t)),
        defaultValues: preSetContent,
    });

    const onSubmit = (data) => {
        if (preSetContent.id) {
            return commentTemplate
                .updateCommentTemplate(preSetContent.id, data)
                .then(() => {
                    SnackbarUtils.success(t('UPDATED_SUCCESSFULLY').toString());
                    closeDialog();
                });
        }

        commentTemplate.createCommentTemplate(data).then(() => {
            SnackbarUtils.success(t('CREATED_SUCCESSFULLY').toString());
            closeDialog();
        });
    };

    useEffect((): void => {
        if (firstRun.current) {
            firstRun.current = false;
            return;
        }

        form.handleSubmit(onSubmit)();
    }, [triggerChild, firstRun.current]);

    return (
        <Stack component="form" flexDirection="column" paddingY={1} gap={1}>
            <FormControl fullWidth>
                <TextFieldControlled
                    autoFocus
                    label={t('NAME')}
                    control={form.control}
                    name="title"
                />
            </FormControl>
            <FormControl fullWidth>
                <TextFieldControlled
                    label={t('CONTENT')}
                    control={form.control}
                    name="note"
                />
            </FormControl>
        </Stack>
    );
};
